import { useRef } from "react"
import { CreateAreaSidebar } from "components/ui/customWebviewer/shared/CreateAreaSidebar/CreateAreaSidebar"
import { FurnitureImage } from "components/ui/customWebviewer/FurnitureSidebar/layout/FurnitureImage/FurnitureImage"
import { ElevationReferenceSelect } from "components/ui/customWebviewer/FurnitureSidebar/layout/ElevationReferenceSelect/ElevationReferenceSelect"
import { DimensionsSection } from "components/ui/customWebviewer/FurnitureSidebar/layout/DimensionsSection/DimensionsSection"
import { CategoriesSection } from "components/ui/customWebviewer/FurnitureSidebar/layout/CategoriesSection/CategoriesSection"
import { CustomizationSection } from "components/ui/customWebviewer/shared/CustomizationSection/CustomizationSection"
import {
	CUSTOM_TOP_LEVEL_CATEGORY,
	CUSTOM_TOP_LEVEL_OPTIONS,
} from "components/ui/customWebviewer/FurnitureSidebar/furniture.constants"
import { CustomModelNumberField } from "./CustomModelNumberField/CustomModelNumberField"
import { useCreateCustomForm } from "./useCreateCustomForm.hook"
import type { TCustomCreateDetails } from "components/ui/customWebviewer/interactions/useCustomObjectCreate"
import type { TEref } from "types/elevationReferences.type"
import type { DefaultOptionType } from "antd/es/select"
import style from "./CreateCustomSidebar.module.scss"

const { body, divider } = style

// An object's dimensions are inches of a real object: 0 is not a smaller object, it's a broken one.
const MIN_DIMENSION_IN = 1

// The super/object levels are never rendered for "Custom" (CategoriesSection hides them once the
// top level is "Custom"), so there is no list to fetch for them.
const NO_CATEGORY_OPTIONS: DefaultOptionType[] = []
const noop = () => {}

type Props = {
	/**
	 * The area drawn on the drawing, or null while the user still has to draw one — which is the
	 * difference between the panel's two states: the instruction, or the form + footer.
	 */
	area: { pageNumber: number } | null
	/** Elevation references of the page the area was drawn on (erefs are page-bound). */
	pageErefs: TEref[]
	/** The quotation's drawing — custom model numbers are drawing-scoped, not quotation-scoped. */
	drawingId: number | null
	/** The quotation's configured series (`configuration.series_id`), resolved to its display name —
	 * shown read-only in the model-number picker. `null` when the quotation has none configured. */
	seriesName: string | null
	isPending?: boolean
	onCancel: () => void
	onCreate: (details: TCustomCreateDetails) => void
	onClose: () => void
}

/**
 * Right-panel companion of the "add custom object" mode (#1371), rendered through the shared
 * `CreateAreaSidebar` shell: before an area is drawn it only explains the gesture, and once one
 * exists it asks for the object's details.
 *
 * The form is deliberately the furniture EDIT sidebar's own sections, in the design's order —
 * image, elevation reference, model number, dimensions, categories — same precedent
 * `CreateOtherSidebar`/`CreateFurnitureSidebar` already set. A custom object differs from "Other" in
 * two ways the design calls out: the elevation link is mandatory here (not optional, "as for any
 * object"), and it takes its name/price from a picked custom model number rather than typed fields.
 *
 * Customization (REQ-413/414) is wired in exactly as the other create panels do it — the ticket
 * lists it as one of this panel's fields, same as any other create flow.
 *
 * The categories block is a label, not a question: a custom object is BY DEFINITION the one with no
 * category on any level, so the top level is locked to "Custom" and the two below it are not
 * rendered at all.
 */
export const CreateCustomSidebar = ({
	area,
	pageErefs,
	drawingId,
	seriesName,
	isPending = false,
	onCancel,
	onCreate,
	onClose,
}: Props) => {
	const form = useCreateCustomForm(pageErefs)

	// First field of the form, so the shell puts the caret in it the moment the drag lands and the
	// user can carry straight on from the mouse without an extra click.
	const elevationFieldRef = useRef<HTMLDivElement>(null)

	const canCreate = !!area && form.isValid && !isPending

	const handleCreate = () => {
		// Re-checked rather than trusted: `canCreate` is what the button reads, this is what narrows
		// the nullable form fields for the payload.
		if (
			!canCreate ||
			form.width === null ||
			form.height === null ||
			form.depth === null ||
			form.selectedCustomModelNumber === null ||
			form.selectedEref === null
		) {
			return
		}
		onCreate({
			width: form.width,
			height: form.height,
			depth: form.depth,
			customModelNumberId: form.selectedCustomModelNumber.id,
			eref: { id: form.selectedEref.id, version: form.selectedEref.version },
			customization: form.customization.payload,
		})
	}

	return (
		<CreateAreaSidebar
			title="Object"
			hint="Drag on the drawing to define the object area"
			area={area}
			isPending={isPending}
			canSubmit={canCreate}
			submitLabel="Create object"
			onCancel={onCancel}
			onClose={onClose}
			onSubmit={handleCreate}
			focusTargetRef={elevationFieldRef}
		>
			<div className={body}>
				<FurnitureImage imageUrl={null} isCountertop={false} hasModelNumber={false} isCustom />

				<div ref={elevationFieldRef}>
					<ElevationReferenceSelect
						erefs={pageErefs}
						value={form.selectedErefId}
						onChange={form.setSelectedErefId}
						placeholder="Select elevation"
					/>
				</div>

				<div className={divider} />

				<CustomModelNumberField
					drawingId={drawingId}
					value={form.selectedCustomModelNumber}
					onChange={form.selectCustomModelNumber}
					dimensions={{ width: form.width, height: form.height, depth: form.depth }}
					seriesName={seriesName}
				/>

				<div className={divider} />

				<DimensionsSection
					width={form.width}
					height={form.height}
					depth={form.depth}
					onWidthChange={form.setWidth}
					onHeightChange={form.setHeight}
					onDepthChange={form.setDepth}
					min={MIN_DIMENSION_IN}
				/>

				<div className={divider} />

				<CustomizationSection
					isExpanded={form.customization.isExpanded}
					note={form.customization.note}
					priceAdjustment={form.customization.priceAdjustment}
					onExpand={form.customization.expand}
					onRemove={form.customization.collapse}
					onNoteChange={form.customization.setNote}
					onPriceAdjustmentChange={form.customization.setPriceAdjustment}
				/>

				<div className={divider} />

				<CategoriesSection
					topLevelValue={CUSTOM_TOP_LEVEL_CATEGORY}
					topLevelOptions={CUSTOM_TOP_LEVEL_OPTIONS}
					onTopLevelChange={noop}
					superValue={null}
					superOptions={NO_CATEGORY_OPTIONS}
					onSuperChange={noop}
					modelCategoryValue={null}
					modelCategoryOptions={NO_CATEGORY_OPTIONS}
					onModelCategoryChange={noop}
					topLevelDisabled
				/>
			</div>
		</CreateAreaSidebar>
	)
}
